import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so repeated renders / components don't re-request.
const memCache = new Map(); // key: `${target}::${text}` -> translated

/**
 * useTranslate — batch-translate an array of English strings into the active
 * language (only when it differs from English). Returns the translated array
 * (falls back to originals while loading or on error), plus a `showOriginal`
 * toggle so users can flip back to the source text.
 *
 * Usage:
 *   const { output, loading, showOriginal, setShowOriginal, isTranslated } =
 *     useTranslate([intro, ...sections], language);
 */
export default function useTranslate(texts, language, { context } = {}) {
  const target = language === 'es' ? 'es' : 'en';
  const list = useMemo(() => (Array.isArray(texts) ? texts : [texts]).map((t) => t || ''), [texts]);
  const [translated, setTranslated] = useState(list);
  const [loading, setLoading] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    if (target === 'en') { setTranslated(list); return undefined; }

    let cancelled = false;
    const cachedAll = list.map((t) => memCache.get(`${target}::${t}`));
    if (cachedAll.every((v) => v !== undefined)) {
      setTranslated(cachedAll);
      return undefined;
    }

    setLoading(true);
    axios.post(`${BACKEND_URL}/api/translate`, { texts: list, target, context })
      .then((r) => {
        if (cancelled) return;
        const out = r.data?.translations || list;
        out.forEach((tr, i) => memCache.set(`${target}::${list[i]}`, tr));
        setTranslated(out);
      })
      .catch(() => { if (!cancelled) setTranslated(list); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, JSON.stringify(list)]);

  const isTranslated = target !== 'en';
  const output = (!isTranslated || showOriginal) ? list : translated;

  return { output, loading, showOriginal, setShowOriginal, isTranslated };
}
